import { experience } from '@logto/schemas';
import classNames from 'classnames';
import type { TFuncKey } from 'i18next';
import { useContext, useMemo } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import DynamicT from '@/shared/components/DynamicT';
import MatrixLoader from '@/shared/components/MatrixLoader';
import { getBrandingLogoUrl, isLightLogoInDark, lightLogoBackdropClass } from '@/shared/utils/logo';

/**
 * Starts the orbit at the phase it would have if it had been running since the document loaded,
 * so a screen that remounts (chunk fallback, callback page, the pre-boot copy in index.html)
 * carries on circling instead of snapping back to its first dot.
 */
// eslint-disable-next-line @typescript-eslint/ban-types -- React calls ref callbacks with null
const alignOrbitToDocumentClock = (anchor: HTMLDivElement | null) => {
  const loader = anchor?.querySelector<HTMLElement>('[data-slot="matrix-loader"]');

  if (!loader) {
    return;
  }

  const cycle = Number.parseFloat(getComputedStyle(loader).getPropertyValue('--matrix-cycle'));

  if (cycle > 0) {
    loader.style.setProperty('--matrix-phase', `${performance.now() % cycle}ms`);
  }
};

const signInHandOffPaths = ['/callback/', '/direct/', `/${experience.routes.oneTimeToken}`];

const getDefaultMessage = (): TFuncKey => {
  // Tests stub `window.location` without a pathname.
  const { pathname = '' } = window.location;

  return signInHandOffPaths.some((path) => pathname.startsWith(path))
    ? 'description.signing_you_in'
    : 'description.loading';
};

type Props = {
  readonly message?: TFuncKey;
  /** Holds the content back briefly so a fast wait (a cached chunk) does not flash it. */
  readonly isDelayed?: boolean;
};

/**
 * For pages that are nothing but a wait (OAuth/SSO callbacks, direct sign-in, the
 * lazy-chunk fallback). An OAuth return passes through several of these in a row, so it has no
 * entrance animation: identical frames then read as one screen. The loader sits at the exact
 * centre with the logo and status line hung off it, so the pre-boot copy in index.html, which
 * may lack either, hands over without anything moving.
 */
export const LoadingScreen = ({ message, isDelayed = false }: Props) => {
  const { theme, experienceSettings } = useContext(PageContext);
  // Delaying after the pre-boot copy has painted would blank the screen between the two.
  const isHeldBack = useMemo(
    () => isDelayed && !document.querySelector('[data-boot-loading-screen]'),
    [isDelayed]
  );

  const logo =
    experienceSettings &&
    getBrandingLogoUrl({
      theme,
      branding: experienceSettings.branding,
      isDarkModeEnabled: experienceSettings.color.isDarkModeEnabled,
    });

  return (
    <div
      data-slot="loading-screen"
      className="fixed inset-0 z-50 grid place-items-center bg-background"
    >
      <div
        ref={alignOrbitToDocumentClock}
        className={classNames(
          'relative grid place-items-center text-foreground',
          isHeldBack && 'animate-fade-in [animation-delay:200ms]'
        )}
      >
        {logo && (
          <img
            className={classNames(
              'absolute bottom-full left-1/2 mb-10 h-8 w-auto max-w-40 -translate-x-1/2 object-contain',
              isLightLogoInDark(theme, experienceSettings.branding, logo) && lightLogoBackdropClass
            )}
            alt="app logo"
            src={logo}
          />
        )}
        <span aria-hidden className="contents">
          <MatrixLoader />
        </span>
        <p
          role="status"
          data-slot="loading-screen-text"
          className="absolute top-full left-1/2 mt-4 w-max max-w-[calc(100vw-2rem)] -translate-x-1/2 text-center text-sm text-muted"
        >
          <DynamicT forKey={message ?? getDefaultMessage()} />
        </p>
      </div>
    </div>
  );
};
